import { Link, useLocation, useParams, useSearch } from 'wouter';
import useOpenApiClient from '@/hooks/useOpenApiClient';
import { useQuery } from '@tanstack/react-query';
import Close from '@/assets/icons/X.svg?react';
import DebitCard from '@/assets/icons/debit_card.svg?react';
import PrimaryAccountLogo from '@/assets/icons/primary_account_logo.svg?react';
import { Button } from '@/components/ui/button';
import { useMemo } from 'react';
import { cn, ColoredTileIndex, indexColorMap } from '@/lib/utils';
import { AccountActivities } from '@/components/account-details/AccountActivities';
import { formatDate } from 'date-fns';
import AccountDetailsSheet from '@/components/account-list/AccountDetailsSheet';
import { SampleAccountModal } from '@/components/account-details/SampleAccountModal';

export default function AccountDetails() {
  const { apiFetch } = useOpenApiClient();
  const { accountId } = useParams<'account/:accountId'>();
  const searchString = useSearch();
  const [, setLocation] = useLocation();

  const accountIdToEdit = useMemo(
    () => (new URLSearchParams(searchString).get('edit') === 'true' ? accountId : null),
    [searchString, accountId],
  );

  const accountsQuery = useQuery({
    queryKey: ['v1', 'accounts'],
    queryFn: () => apiFetch?.GET('/accounts'),
    select: (data) => data.data?.data,
  });

  const accountsDetailsQuery = useQuery({
    queryKey: ['v1', 'accounts', accountId],
    queryFn: () =>
      apiFetch.GET('/accounts/{account_id}', {
        params: {
          path: {
            account_id: accountId,
          },
        },
      }),
    select: (data) => data.data?.data,
  });

  const accountCreatedAt = useMemo(() => {
    if (!accountsDetailsQuery.data?.created_at) {
      return null;
    }
    return formatDate(new Date(accountsDetailsQuery.data.created_at), 'MM/dd/yyyy');
  }, [accountsDetailsQuery.data?.created_at]);

  const selectedAccountIndexInAccounts =
    accountsQuery.data?.findIndex((account) => account.id === accountsDetailsQuery?.data?.id) ?? 0;

  const accountIsLoyaltyOrBenefitProgram =
    accountsDetailsQuery?.data?.type === 'loyalty_program' ||
    accountsDetailsQuery?.data?.type === 'benefit_program';

  return (
    <div
      className={cn(
        'bg-primary-soft-peach flex max-h-svh min-h-svh flex-col overflow-hidden p-2.5',
      )}
    >
      <div className="flex justify-end">
        <Link href="/">
          <Button variant="ghost" size="icon" className="[&_svg]:size-auto">
            <Close />
          </Button>
        </Link>
      </div>
      {accountsDetailsQuery.data && (
        <div className="flex flex-col p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-foreground text-2xl font-normal">
                {accountsDetailsQuery.data.nickname}
              </h2>
              <Link href={`?edit=true`} replace className="text-primary text-xs font-bold">
                Edit nickname
              </Link>
            </div>
            <span className="relative">
              {accountsDetailsQuery.data.is_primary ? (
                <>
                  <PrimaryAccountLogo className="rotate-[-9.8deg]" />
                </>
              ) : (
                <>
                  {!accountIsLoyaltyOrBenefitProgram && (
                    <DebitCard className="absolute top-[20%] -left-[54%]" />
                  )}
                  <span
                    className={cn(
                      'border-primary-dark text-foreground flex h-14 w-14 rotate-[-9.8deg] items-center justify-center rounded-full border text-5xl leading-[46.047px] font-normal lowercase opacity-80 shadow-[-2px_4px_0px_#3C451D]',
                      indexColorMap.index[
                        (selectedAccountIndexInAccounts % 12) as ColoredTileIndex
                      ],
                    )}
                  >
                    {accountsDetailsQuery.data.nickname[0]}
                  </span>
                </>
              )}
            </span>
          </div>
          <p className="text-foreground mt-1 text-xs font-normal">
            {accountsDetailsQuery.data?.issued_by}
          </p>
          <p className="text-foreground text-xs font-normal">
            Added on {accountCreatedAt}
            <br />
            {accountsDetailsQuery.data.external_account_number_last_4 ? (
              <>Ends in ***{accountsDetailsQuery.data.external_account_number_last_4}</>
            ) : null}
          </p>
        </div>
      )}
      {accountsDetailsQuery.data && (
        <AccountActivities account={{ ...accountsDetailsQuery.data }} />
      )}
      {accountsDetailsQuery.data?.is_demo && <SampleAccountModal />}
      <AccountDetailsSheet
        onClose={() => setLocation(`/account/${accountId}`)}
        accountId={accountIdToEdit}
      />
    </div>
  );
}
